// RecruitmentFlowComponent.js — Full-featured Recruitment Flow
// Real API · Full pipeline · Analytics · Candidate management · AI scoring · Scheduler

/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Dimensions, StatusBar,
  ScrollView, RefreshControl, Modal, FlatList, Alert, ActivityIndicator,
  Animated, TextInput, KeyboardAvoidingView, Platform, Share, Switch, Image,
} from 'react-native';
import { MaterialCommunityIcons, FontAwesome5, Ionicons, AntDesign } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width, height } = Dimensions.get('window');

// ── API ───────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const api = async (path, method = 'GET', body = null) => {
  const token = await AsyncStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw { status: res.status, ...e }; }
  return res.json();
};

// ── Constants ─────────────────────────────────────────────────────────────────
const STAGES = [
  { id: 'job_posting',    label: 'Job Posting',          icon: 'briefcase-outline',             color: '#3B82F6', statKey: 'activeJobs'    },
  { id: 'applicants',     label: 'Applicants',            icon: 'account-multiple-outline',      color: '#10B981', statKey: 'totalApplicants'},
  { id: 'screening',      label: 'Screening',             icon: 'filter-outline',                color: '#F59E0B', statKey: 'screened'      },
  { id: 'shortlisted',    label: 'Shortlisted',           icon: 'account-star-outline',          color: '#8B5CF6', statKey: 'shortlisted'   },
  { id: 'assessment',     label: 'Assessment',            icon: 'clipboard-check-outline',       color: '#EF4444', statKey: 'assessed'      },
  { id: 'video_interview',label: 'Video Interview',       icon: 'video-outline',                 color: '#0EA5E9', statKey: 'videoInt'      },
  { id: 'final_interview',label: 'Final Interview',       icon: 'account-tie-outline',           color: '#DC2626', statKey: 'finalInt'      },
  { id: 'offered',        label: 'Offer Sent',            icon: 'file-document-outline',         color: '#7C3AED', statKey: 'offered'       },
  { id: 'accepted',       label: 'Offer Accepted',        icon: 'check-decagram-outline',        color: '#059669', statKey: 'accepted'      },
  { id: 'hired',          label: 'Hired',                 icon: 'account-check',                 color: '#0891B2', statKey: 'hired'         },
];

const STAGE_SCREENS = {
  job_posting:    'PostJobScreen',
  applicants:     'ApplicantsPoolScreen',
  screening:      'ApplicantSelectionScreen',
  shortlisted:    'QualifiedApplicantsScreen',
  assessment:     'TestApplicantsScreen',
  video_interview:'VideoScreen',
  final_interview:'InPersonInterviewScreen',
  offered:        'JobOfferManagementScreen',
  accepted:       'AcceptedOffersScreen',
  hired:          'HiredScreen',
};

const PRIORITY_COLORS = { high: '#EF4444', medium: '#F59E0B', low: '#10B981' };
const STATUS_COLORS   = { active: '#10B981', inactive: '#EF4444', pending: '#F59E0B', online: '#10B981', away: '#F59E0B', offline: '#94A3B8' };

// ── Helpers ───────────────────────────────────────────────────────────────────
const safe = (v, fb = '') => (v != null && v !== '' ? String(v) : fb);
const safeNum = (v, fb = 0) => { const n = parseFloat(v); return isNaN(n) ? fb : n; };
const safeArr = (v) => (Array.isArray(v) ? v : []);
const initials = (name = '') => name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtTime  = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
const timeAgo  = (ts) => {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : 0);
const AVATAR_COLORS = ['#3B82F6','#10B981','#8B5CF6','#F59E0B','#EF4444','#0EA5E9','#EC4899','#14B8A6'];
const avatarColor = (name = '') => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

// ── Sub-components ─────────────────────────────────────────────────────────────
const Avatar = ({ name, size = 40, color }) => (
  <View style={{ width: size, height: size, borderRadius: size / 2,
    backgroundColor: color || avatarColor(name), alignItems: 'center', justifyContent: 'center' }}>
    <Text style={{ color: '#fff', fontSize: size * 0.35, fontWeight: '800' }}>{initials(name)}</Text>
  </View>
);

const Pill = ({ label, color, bg }) => (
  <View style={{ backgroundColor: bg || color + '20', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 3 }}>
    <Text style={{ fontSize: 10, fontWeight: '700', color: color }}>{label}</Text>
  </View>
);

const ScoreRing = ({ score, size = 44, color = '#3B82F6' }) => {
  const label = score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'Low';
  const ringColor = score >= 80 ? '#059669' : score >= 60 ? '#3B82F6' : score >= 40 ? '#F59E0B' : '#EF4444';
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, borderWidth: 3,
      borderColor: ringColor, alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ fontSize: size * 0.3, fontWeight: '900', color: ringColor }}>{score}</Text>
    </View>
  );
};

const FunnelBar = ({ label, value, total, color, onPress }) => {
  const w = total > 0 ? Math.max((value / total) * 100, 2) : 0;
  return (
    <TouchableOpacity onPress={onPress} style={{ marginBottom: 8 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 3 }}>
        <Text style={{ fontSize: 12, color: '#64748B', width: 120 }} numberOfLines={1}>{label}</Text>
        <View style={{ flex: 1, height: 18, backgroundColor: '#F1F5F9', borderRadius: 4, overflow: 'hidden', marginHorizontal: 8 }}>
          <View style={{ width: `${w}%`, height: '100%', backgroundColor: color, borderRadius: 4 }} />
        </View>
        <Text style={{ fontSize: 12, fontWeight: '700', color, width: 36, textAlign: 'right' }}>{value}</Text>
        <Text style={{ fontSize: 10, color: '#94A3B8', width: 34, textAlign: 'right' }}>{pct(value, total)}%</Text>
      </View>
    </TouchableOpacity>
  );
};

// ── MAIN SCREEN ───────────────────────────────────────────────────────────────
export default function RecruitmentFlowComponent({ navigation, route }) {
  const [companyId, setCompanyId] = useState(null);
  const [userId, setUserId]       = useState(null);
  const [userName, setUserName]   = useState('');

  // Data
  const [stats, setStats]             = useState({ activeJobs:0, totalApplicants:0, screened:0, shortlisted:0, assessed:0, videoInt:0, finalInt:0, offered:0, accepted:0, hired:0, rejected:0, totalJobs:0, newToday:0 });
  const [jobs, setJobs]               = useState([]);
  const [candidates, setCandidates]   = useState([]);
  const [interviews, setInterviews]   = useState([]);
  const [offers, setOffers]           = useState([]);
  const [activities, setActivities]   = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  // UI
  // ── Company logo ──────────────────────────────────────────────────────────
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [activeTab, setActiveTab]     = useState('overview'); // overview | pipeline | jobs | analytics | team
  const [searchQ, setSearchQ]         = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterDept, setFilterDept]   = useState('all');
  const [sortBy, setSortBy]           = useState('date');
  const [showSearch, setShowSearch]   = useState(false);

  // Modals
  const [stageModal, setStageModal]         = useState(null);   // stage id
  const [candidateModal, setCandidateModal] = useState(null);   // candidate obj
  const [statsModal, setStatsModal]         = useState(false);
  const [activitiesModal, setActivitiesModal] = useState(false);
  const [teamModal, setTeamModal]           = useState(false);
  const [jobDetailModal, setJobDetailModal] = useState(null);
  const [scheduleModal, setScheduleModal]   = useState(false);
  const [bulkModal, setBulkModal]           = useState(false);
  const [notesModal, setNotesModal]         = useState(null);   // candidate id
  const [noteText, setNoteText]             = useState('');
  const [savingNote, setSavingNote]         = useState(false);

  // Bulk select
  const [bulkSelected, setBulkSelected]     = useState(new Set());
  const [bulkMode, setBulkMode]             = useState(false);

  const progressAnim = useRef(new Animated.Value(0)).current;

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  const loadCompanyProfile = async () => {
    try {
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const cp = JSON.parse(cached);
        setCompanyName(cp.company_name || cp.name || '');
        const l = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (l) setCompanyLogo(l);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (!cid) return;
      const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      const res = await fetch(`https://api.jobeng.ai/api/data/company_profiles?company_id=${cid}`,
        { headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      if (!res.ok) return;
      const r = await res.json();
      const cp = Array.isArray(r) ? r[0] : (r.data?.[0] ?? r.data ?? r);
      if (cp) {
        setCompanyName(cp.company_name || cp.name || '');
        const l = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (l) setCompanyLogo(l);
        await AsyncStorage.setItem('company_profile', JSON.stringify(cp));
      }
    } catch {}
  };

  useEffect(() => {
    AsyncStorage.multiGet(['company_id','user_id','user_name']).then(pairs => {
      const m = Object.fromEntries(pairs);
      if (m.company_id) setCompanyId(m.company_id);
      if (m.user_id)    setUserId(m.user_id);
      if (m.user_name)  setUserName(m.user_name);
    });
  }, []);

  useEffect(() => { if (companyId) loadAll(true); }, [companyId]);

  // ── Load all data ──────────────────────────────────────────────────────────
  const loadAll = useCallback(async (spinner = true) => {
    if (spinner) setLoading(true);
    try {
      await Promise.allSettled([
        fetchJobs(),
        fetchApplications(),
        fetchInterviews(),
        fetchOffers(),
        fetchActivities(),
        fetchTeam(),
      ]);
      Animated.timing(progressAnim, { toValue: 1, duration: 800, useNativeDriver: false }).start();
    } catch (e) {
      console.warn('loadAll error:', e?.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  const onRefresh = useCallback(() => { setRefreshing(true); loadAll(false); }, [loadAll]);

  const fetchJobs = async () => {
    try {
      const r = await api(`/api/data/jobs?company_id=${companyId}&status=active`);
      const data = safeArr(Array.isArray(r) ? r : r.data);
      setJobs(data);
      const active = data.filter(j => j.status === 'active' || j.is_active).length;
      setStats(s => ({ ...s, totalJobs: data.length, activeJobs: active }));
    } catch {}
  };

  const fetchApplications = async () => {
    try {
      const r = await api(`/api/data/applications?company_id=${companyId}`);
      const data = safeArr(Array.isArray(r) ? r : r.data);
      setCandidates(data);
      const byStage = (stage) => data.filter(c => (c.stage || c.status || 'applicants') === stage).length;
      setStats(s => ({
        ...s,
        totalApplicants: data.length,
        screened:    byStage('screening'),
        shortlisted: byStage('shortlisted'),
        assessed:    byStage('assessment'),
        videoInt:    byStage('video_interview'),
        finalInt:    byStage('final_interview'),
        offered:     byStage('offered'),
        accepted:    byStage('accepted'),
        hired:       byStage('hired'),
        rejected:    data.filter(c => c.stage === 'rejected' || c.status === 'rejected').length,
        newToday:    data.filter(c => new Date(c.created_at) > new Date(Date.now() - 86400000)).length,
      }));
    } catch {}
  };

  const fetchInterviews = async () => {
    try {
      const r = await api(`/api/data/interviews?company_id=${companyId}&scheduled=1`);
      setInterviews(safeArr(Array.isArray(r) ? r : r.data));
    } catch {}
  };

  const fetchOffers = async () => {
    try {
      const r = await api(`/api/data/job_offers?company_id=${companyId}`);
      setOffers(safeArr(Array.isArray(r) ? r : r.data));
    } catch {}
  };

  const fetchActivities = async () => {
    try {
      const r = await api(`/api/data/recruitment_activities?company_id=${companyId}&limit=30`);
      setActivities(safeArr(Array.isArray(r) ? r : r.data));
    } catch { setActivities([]); }
  };

  const fetchTeam = async () => {
    try {
      const r = await api(`/api/data/team_members?company_id=${companyId}`);
      setTeamMembers(safeArr(Array.isArray(r) ? r : r.data));
    } catch {}
  };

  // ── Candidate actions ──────────────────────────────────────────────────────
  const advanceCandidate = async (candidateId, nextStage) => {
    try {
      await api(`/api/recruitment/applications/${candidateId}/stage`, 'PUT', {
        stage: nextStage, notes: 'Advanced via pipeline',
      });
      await fetchApplications();
      Alert.alert('✅ Advanced', `Candidate moved to ${nextStage.replace('_', ' ')}`);
    } catch { Alert.alert('Error', 'Could not advance candidate.'); }
  };

  const rejectCandidate = (candidateId, name) => {
    Alert.alert(`Reject ${name}?`, 'This will move the candidate to rejected.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Reject', style: 'destructive', onPress: async () => {
        try {
          await api(`/api/recruitment/applications/${candidateId}/reject`, 'PUT', { reason: 'Not selected' });
          setCandidateModal(null);
          await fetchApplications();
        } catch { Alert.alert('Error', 'Could not reject candidate.'); }
      }},
    ]);
  };

  const shortlistCandidate = async (candidateId) => {
    try {
      await api(`/api/recruitment/applications/${candidateId}/stage`, 'PUT', { stage: 'shortlisted', notes: 'Shortlisted' });
      await fetchApplications();
    } catch {}
  };

  const saveNote = async (candidateId) => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await api(`/api/data/applications/${candidateId}`, 'PUT', {
        notes: noteText.trim(), updated_at: new Date().toISOString(),
      });
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, notes: noteText.trim() } : c));
      setNotesModal(null);
      setNoteText('');
    } catch { Alert.alert('Error', 'Could not save note.'); }
    finally { setSavingNote(false); }
  };

  const bulkAction = async (action) => {
    if (!bulkSelected.size) return;
    const ids = [...bulkSelected];
    try {
      await api('/api/recruitment/applications/bulk-stage', 'PUT', {
        application_ids: ids, stage: action === 'advance' ? 'shortlisted' : action === 'reject' ? 'rejected' : action,
      });
      setBulkSelected(new Set());
      setBulkMode(false);
      setBulkModal(false);
      await fetchApplications();
      Alert.alert('Done', `${ids.length} candidate(s) updated.`);
    } catch { Alert.alert('Error', 'Some updates failed.'); }
  };

  const scheduleInterview = async (candidateId, type = 'video') => {
    navigation?.navigate(type === 'video' ? 'BusinessVideoConferenceScreen' : 'InPersonInterviewScreen', {
      candidateId, companyId,
    });
  };

  const exportReport = () => {
    const lines = [
      `Recruitment Pipeline Report — ${new Date().toLocaleDateString()}`,
      `Company: ${companyId}`,
      '',
      `Total Jobs: ${stats.totalJobs}    Active: ${stats.activeJobs}`,
      `Total Applicants: ${stats.totalApplicants}`,
      `Hired: ${stats.hired}    Rejected: ${stats.rejected}`,
      `Hire Rate: ${pct(stats.hired, stats.totalApplicants)}%`,
      '',
      'FUNNEL:',
      ...STAGES.map(s => `  ${s.label}: ${stats[s.statKey] || 0}`),
    ];
    Share.share({ message: lines.join('\n'), title: 'Recruitment Report' });
  };

  // ── Navigation ─────────────────────────────────────────────────────────────
  const goTo = (screen, params = {}) => {
    navigation?.navigate(screen, { companyId, companyLogo, companyName, ...params });
  };

  const handleStagePress = (stage) => {
    const screen = STAGE_SCREENS[stage.id];
    if (screen) goTo(screen);
  };

  // ── Computed ───────────────────────────────────────────────────────────────
  const filteredCandidates = useMemo(() => {
    let list = [...candidates];
    if (filterStage !== 'all') list = list.filter(c => (c.stage || c.status) === filterStage);
    if (searchQ) {
      const q = searchQ.toLowerCase();
      list = list.filter(c => [c.candidate_name, c.job_title, c.email, c.department]
        .some(v => (v || '').toLowerCase().includes(q)));
    }
    if (filterDept !== 'all') list = list.filter(c => c.department === filterDept);
    list.sort((a, b) => {
      if (sortBy === 'date') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'score') return safeNum(b.ai_score) - safeNum(a.ai_score);
      if (sortBy === 'name') return (a.candidate_name || '').localeCompare(b.candidate_name || '');
      return 0;
    });
    return list;
  }, [candidates, filterStage, searchQ, filterDept, sortBy]);

  const todayInterviews = interviews.filter(i => {
    const d = new Date(i.interview_date || i.date);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  });

  const pendingOffers = offers.filter(o => o.status === 'pending' || o.status === 'sent');

  const hireRate   = pct(stats.hired, stats.totalApplicants);
  const offerRate  = pct(stats.offered, stats.totalApplicants);
  const screenRate = pct(stats.shortlisted, stats.totalApplicants);

  const departments = [...new Set(candidates.map(c => c.department).filter(Boolean))];

  // ── Render helpers ─────────────────────────────────────────────────────────
  const renderPendingStrip = () => {
    const items = [];
    if (stats.newToday > 0)       items.push({ label: `${stats.newToday} new today`, color: '#10B981', icon: 'account-plus' });
    if (todayInterviews.length > 0) items.push({ label: `${todayInterviews.length} interviews today`, color: '#3B82F6', icon: 'calendar-clock' });
    if (pendingOffers.length > 0)  items.push({ label: `${pendingOffers.length} offers pending`, color: '#7C3AED', icon: 'file-document' });
    if (!items.length) return null;
    return (
      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        style={{ paddingVertical: 8 }} contentContainerStyle={{ paddingHorizontal: 20, gap: 8 }}>
        {items.map((item, i) => (
          <View key={`pending-${i}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 5,
            backgroundColor: item.color + '15', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 6,
            borderWidth: 1, borderColor: item.color + '30' }}>
            <MaterialCommunityIcons name={item.icon} size={13} color={item.color} />
            <Text style={{ fontSize: 12, color: item.color, fontWeight: '700' }}>{item.label}</Text>
          </View>
        ))}
      </ScrollView>
    );
  };

  const renderStatCard = (label, value, icon, color, onPress) => (
    <TouchableOpacity style={[S.statCard, { borderLeftColor: color }]} onPress={onPress} activeOpacity={0.8}>
      <View style={[S.statIcon, { backgroundColor: color + '18' }]}>
        <MaterialCommunityIcons name={icon} size={22} color={color} />
      </View>
      <Text style={S.statValue}>{value}</Text>
      <Text style={S.statLabel}>{label}</Text>
    </TouchableOpacity>
  );

  // ── Tab: Overview ──────────────────────────────────────────────────────────
  const renderOverview = () => (
    <View>
      {/* KPI cards */}
      <View style={S.kpiRow}>
        {[
          { label: 'Active Jobs',    value: stats.activeJobs,      icon: 'briefcase',            color: '#3B82F6' },
          { label: 'Applicants',     value: stats.totalApplicants, icon: 'account-group',        color: '#10B981' },
          { label: 'Interviews',     value: stats.videoInt + stats.finalInt, icon: 'calendar-clock', color: '#F59E0B' },
          { label: 'Hired',          value: stats.hired,           icon: 'check-circle',         color: '#059669' },
        ].map((k, i) => <React.Fragment key={k.label}>{renderStatCard(k.label, k.value, k.icon, k.color, () => setStatsModal(true))}</React.Fragment>)}
      </View>

      {/* Pending actions strip */}
      {renderPendingStrip()}

      {/* AI Automation Card */}
      <TouchableOpacity style={S.aiCard} onPress={() => goTo('AutomatedHiringPipeline')} activeOpacity={0.9}>
        <LinearGradient colors={['#FF6B35','#F7931E']} style={S.aiGradient}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <View style={{ width: 52, height: 52, borderRadius: 26, backgroundColor: 'rgba(255,255,255,0.2)',
              alignItems: 'center', justifyContent: 'center' }}>
              <MaterialCommunityIcons name="robot" size={28} color="#fff" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ color: '#fff', fontSize: 16, fontWeight: '900' }}>AI Hiring Pipeline</Text>
              <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, marginTop: 2 }}>
                Automate screening · scoring · matching
              </Text>
            </View>
            <View style={S.aiLaunchBtn}>
              <Text style={{ color: '#FF6B35', fontWeight: '800', fontSize: 12 }}>Launch</Text>
              <MaterialCommunityIcons name="arrow-right" size={14} color="#FF6B35" />
            </View>
          </View>
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {['AI Resume Screening','Auto Scoring','Video Analysis','Predictive Match'].map(f => (
              <View key={`ai-feat-${f}`} style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 12,
                paddingHorizontal: 10, paddingVertical: 4 }}>
                <Text style={{ color: '#fff', fontSize: 10, fontWeight: '600' }}>{f}</Text>
              </View>
            ))}
          </View>
        </LinearGradient>
      </TouchableOpacity>

      {/* Hiring Funnel */}
      <View style={S.card}>
        <View style={S.cardHeader}>
          <Text style={S.cardTitle}>Hiring Funnel</Text>
          <TouchableOpacity onPress={() => setActiveTab('analytics')}>
            <Text style={S.seeAll}>Full Analytics →</Text>
          </TouchableOpacity>
        </View>
        {STAGES.map(stage => (
          <FunnelBar key={`funnel-${stage.id}`}
            label={stage.label}
            value={stats[stage.statKey] || 0}
            total={stats.totalApplicants}
            color={stage.color}
            onPress={() => { setStageModal(stage.id); }}
          />
        ))}
        <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginTop: 14,
          paddingTop: 10, borderTopWidth: 1, borderTopColor: '#F1F5F9' }}>
          {[
            { label: 'Screen Rate', value: `${screenRate}%`, color: '#8B5CF6' },
            { label: 'Offer Rate',  value: `${offerRate}%`,  color: '#7C3AED' },
            { label: 'Hire Rate',   value: `${hireRate}%`,   color: '#059669' },
          ].map(m => (
            <View key={`metric-${m.label}`} style={{ alignItems: 'center' }}>
              <Text style={{ fontSize: 20, fontWeight: '900', color: m.color }}>{m.value}</Text>
              <Text style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{m.label}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Today's Interviews */}
      {todayInterviews.length > 0 && (
        <View style={S.card}>
          <View style={S.cardHeader}>
            <Text style={S.cardTitle}>📅 Today's Interviews ({todayInterviews.length})</Text>
            <TouchableOpacity onPress={() => goTo('InterviewManagementScreen')}>
              <Text style={S.seeAll}>All →</Text>
            </TouchableOpacity>
          </View>
          {todayInterviews.slice(0, 3).map((iv, i) => (
            <View key={iv.id ? `iv-${iv.id}` : `iv-${i}`} style={S.interviewRow}>
              <Avatar name={iv.candidate_name || '?'} size={36} />
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={S.interviewName}>{iv.candidate_name || 'Candidate'}</Text>
                <Text style={S.interviewMeta}>{iv.job_title || '—'} · {fmtTime(iv.interview_date || iv.date)}</Text>
              </View>
              <TouchableOpacity style={[S.smallBtn, { backgroundColor: '#3B82F6' }]}
                onPress={() => goTo('BusinessVideoConferenceScreen', { meetingTitle: `Interview: ${iv.candidate_name}` })}>
                <Ionicons name="videocam" size={13} color="#fff" />
                <Text style={S.smallBtnText}>Join</Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}

      {/* Pending Offers */}
      {pendingOffers.length > 0 && (
        <View style={S.card}>
          <View style={S.cardHeader}>
            <Text style={S.cardTitle}>📋 Pending Offers ({pendingOffers.length})</Text>
            <TouchableOpacity onPress={() => goTo('JobOfferManagementScreen')}>
              <Text style={S.seeAll}>Manage →</Text>
            </TouchableOpacity>
          </View>
          {pendingOffers.slice(0, 3).map((o, i) => (
            <View key={o.id ? `offer-${o.id}` : `offer-${i}`} style={S.offerRow}>
              <Avatar name={o.candidate_name || '?'} size={34} />
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={S.interviewName}>{o.candidate_name || '—'}</Text>
                <Text style={S.interviewMeta}>{o.job_title || '—'} · Sent {timeAgo(o.created_at)}</Text>
              </View>
              <Pill label="Pending" color="#F59E0B" />
            </View>
          ))}
        </View>
      )}

      {/* Recent Activities */}
      <View style={S.card}>
        <View style={S.cardHeader}>
          <Text style={S.cardTitle}>Recent Activity</Text>
          <TouchableOpacity onPress={() => setActivitiesModal(true)}>
            <Text style={S.seeAll}>View All →</Text>
          </TouchableOpacity>
        </View>
        {(activities.length > 0 ? activities : getDefaultActivities()).slice(0, 5).map((a, i) => (
          <View key={a.id ? `act-${a.id}` : `act-${i}`} style={S.activityRow}>
            <View style={[S.activityDot, { backgroundColor: a.color || '#3B82F6' }]}>
              <MaterialCommunityIcons name={a.icon || 'information'} size={13} color="#fff" />
            </View>
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={S.activityTitle}>{a.title}</Text>
              <Text style={S.activityDesc} numberOfLines={1}>{a.description}</Text>
            </View>
            <Text style={S.activityTime}>{timeAgo(a.timestamp || a.created_at || new Date())}</Text>
          </View>
        ))}
      </View>

      {/* Jobeng Feature Cards */}
      <View style={S.card}>
        <Text style={S.cardTitle}>Platform Features</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
          {[
            { icon: 'robot', label: 'AI Automation', color: '#FF6B35', bg: '#FFE5D9', screen: 'AutomatedHiringPipeline' },
            { icon: 'brain', label: 'Smart Matching', color: '#0891B2', bg: '#E0F2FE', screen: 'ApplicantSelectionScreen' },
            { icon: 'shield-check', label: 'Background Check', color: '#7C3AED', bg: '#F3E8FF', screen: 'ComplianceCheckScreen' },
            { icon: 'file-sign', label: 'Smart Contracts', color: '#059669', bg: '#DCFCE7', screen: 'SmartContractTemplates' },
            { icon: 'chart-timeline', label: 'Pipeline Analytics', color: '#DC2626', bg: '#FEE2E2', screen: 'CompanyAnalytics' },
            { icon: 'video', label: 'Video Interviews', color: '#0EA5E9', bg: '#E0F2FE', screen: 'VideoScreen' },
            { icon: 'school', label: 'Assessments', color: '#F59E0B', bg: '#FEF9C3', screen: 'TestApplicantsScreen' },
            { icon: 'account-network', label: 'Networking', color: '#8B5CF6', bg: '#EDE9FE', screen: 'NetworkingScreen' },
          ].map(f => (
            <TouchableOpacity key={`feat-${f.label}`} style={[S.featureChip, { backgroundColor: f.bg, borderColor: f.color + '30' }]}
              onPress={() => goTo(f.screen)}>
              <MaterialCommunityIcons name={f.icon} size={16} color={f.color} />
              <Text style={{ fontSize: 11, color: f.color, fontWeight: '700', marginLeft: 5 }}>{f.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </View>
  );

  // ── Tab: Pipeline ──────────────────────────────────────────────────────────
  const renderPipeline = () => (
    <View>
      {/* Search + Filter bar */}
      <View style={S.filterBar}>
        <TouchableOpacity style={S.searchTrigger} onPress={() => setShowSearch(true)}>
          <Ionicons name="search" size={16} color="#94A3B8" />
          <Text style={{ color: '#94A3B8', fontSize: 14, marginLeft: 6 }}>
            {searchQ || 'Search candidates…'}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity style={S.filterBtn}
          onPress={() => setBulkMode(v => { if (v) setBulkSelected(new Set()); return !v; })}>
          <Ionicons name={bulkMode ? 'checkmark-circle' : 'checkmark-circle-outline'} size={20}
            color={bulkMode ? '#3B82F6' : '#64748B'} />
        </TouchableOpacity>
        <TouchableOpacity style={S.filterBtn} onPress={exportReport}>
          <Ionicons name="share-outline" size={20} color="#64748B" />
        </TouchableOpacity>
      </View>

      {/* Stage filter chips */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 16, gap: 7, paddingVertical: 8 }}>
        {[{ id: 'all', label: 'All', color: '#64748B' }, ...STAGES].map(s => (
          <TouchableOpacity key={`sfilt-${s.id}`}
            style={[S.stageChip, filterStage === s.id && { backgroundColor: s.color, borderColor: s.color }]}
            onPress={() => setFilterStage(s.id)}>
            <Text style={[S.stageChipText, filterStage === s.id && { color: '#fff' }]}>
              {s.label} {s.id !== 'all' && stats[s.statKey] > 0 ? `(${stats[s.statKey]})` : ''}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Sort bar */}
      <View style={{ flexDirection: 'row', paddingHorizontal: 16, gap: 6, marginBottom: 8 }}>
        {[['date','Date'],['score','AI Score'],['name','Name']].map(([k, l]) => (
          <TouchableOpacity key={`sort-${k}`} onPress={() => setSortBy(k)}
            style={[S.sortBtn, sortBy === k && S.sortBtnActive]}>
            <Text style={[S.sortBtnText, sortBy === k && { color: '#3B82F6' }]}>{l}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Bulk action bar */}
      {bulkMode && bulkSelected.size > 0 && (
        <View style={S.bulkBar}>
          <Text style={{ color: '#fff', fontWeight: '700' }}>{bulkSelected.size} selected</Text>
          <TouchableOpacity style={S.bulkAction} onPress={() => bulkAction('shortlisted')}>
            <Text style={S.bulkActionText}>Shortlist</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[S.bulkAction, { backgroundColor: '#EF4444' }]} onPress={() => bulkAction('rejected')}>
            <Text style={S.bulkActionText}>Reject</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[S.bulkAction, { backgroundColor: '#0EA5E9' }]} onPress={() => goTo('BusinessVideoConferenceScreen')}>
            <Text style={S.bulkActionText}>Schedule</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Candidate list */}
      {filteredCandidates.length === 0 ? (
        <View style={{ alignItems: 'center', paddingVertical: 48 }}>
          <MaterialCommunityIcons name="account-search" size={54} color="#CBD5E1" />
          <Text style={{ color: '#94A3B8', marginTop: 12, fontSize: 15 }}>No candidates found</Text>
        </View>
      ) : (
        filteredCandidates.map(c => <React.Fragment key={c.id || `rc-${c.candidate_name}`}>{renderCandidateCard(c)}</React.Fragment>)
      )}

      {/* Stage pipeline boxes */}
      <View style={[S.card, { marginTop: 8 }]}>
        <Text style={[S.cardTitle, { marginBottom: 14 }]}>Pipeline Stages</Text>
        {STAGES.map((stage, i) => {
          const stageCount = stats[stage.statKey] || 0;
          // key added below
          const nextStage  = STAGES[i + 1];
          return (
            <View key={stage.id}>
              <TouchableOpacity style={[S.pipelineRow, { borderLeftColor: stage.color }]}
                onPress={() => setStageModal(stage.id)}>
                <View style={[S.pipelineIcon, { backgroundColor: stage.color + '15' }]}>
                  <MaterialCommunityIcons name={stage.icon} size={18} color={stage.color} />
                </View>
                <View style={{ flex: 1, marginLeft: 10 }}>
                  <Text style={S.pipelineName}>{stage.label}</Text>
                  {nextStage && stageCount > 0 && (
                    <Text style={{ fontSize: 10, color: '#94A3B8' }}>
                      {pct(stats[nextStage.statKey] || 0, stageCount)}% advance to {nextStage.label}
                    </Text>
                  )}
                </View>
                <View style={[S.pipelineCount, { backgroundColor: stage.color + '15' }]}>
                  <Text style={[S.pipelineCountText, { color: stage.color }]}>{stageCount}</Text>
                </View>
                <TouchableOpacity style={[S.smallBtn, { backgroundColor: stage.color, marginLeft: 8 }]}
                  onPress={() => goTo(STAGE_SCREENS[stage.id])}>
                  <Text style={[S.smallBtnText, { fontSize: 10 }]}>Open</Text>
                </TouchableOpacity>
              </TouchableOpacity>
              {i < STAGES.length - 1 && (
                <View style={{ alignItems: 'flex-start', paddingLeft: 28, marginVertical: 1 }}>
                  <MaterialCommunityIcons name="arrow-down" size={14} color="#CBD5E1" />
                </View>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );

  const renderCandidateCard = (c) => {
    const stage    = STAGES.find(s => s.id === (c.stage || c.status)) || STAGES[1];
    const aiScore  = safeNum(c.ai_score || c.score);
    const selected = bulkSelected.has(c.id);

    return (
      <TouchableOpacity key={c.id} style={[S.candidateCard, selected && S.candidateCardSelected]}
        onPress={() => { if (bulkMode) { setBulkSelected(prev => { const n = new Set(prev); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n; }); } else { setCandidateModal(c); } }}
        onLongPress={() => { setBulkMode(true); setBulkSelected(new Set([c.id])); }}>
        {bulkMode && (
          <Ionicons name={selected ? 'checkbox' : 'square-outline'} size={20} color={selected ? '#3B82F6' : '#CBD5E1'} style={{ marginRight: 8 }} />
        )}
        <Avatar name={c.candidate_name || '?'} size={44} />
        <View style={{ flex: 1, marginLeft: 10 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Text style={S.candidateName} numberOfLines={1}>{c.candidate_name || '—'}</Text>
            {aiScore > 0 && <ScoreRing score={aiScore} size={28} />}
          </View>
          <Text style={S.candidateRole} numberOfLines={1}>{c.job_title || c.position || '—'}</Text>
          <View style={{ flexDirection: 'row', gap: 5, marginTop: 4, flexWrap: 'wrap' }}>
            <Pill label={stage.label} color={stage.color} />
            {!!c.department && <Pill label={c.department} color="#64748B" />}
            {c.experience_years > 0 && <Pill label={`${c.experience_years}yr exp`} color="#0EA5E9" />}
          </View>
        </View>
        <View style={{ alignItems: 'flex-end', gap: 6 }}>
          <Text style={{ fontSize: 10, color: '#94A3B8' }}>{timeAgo(c.created_at || new Date())}</Text>
          <TouchableOpacity style={[S.smallBtn, { backgroundColor: stage.color }]}
            onPress={() => setCandidateModal(c)}>
            <Text style={S.smallBtnText}>View</Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  // ── Tab: Jobs ──────────────────────────────────────────────────────────────
  const renderJobs = () => (
    <View>
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
        <TouchableOpacity style={[S.primaryBtn, { flex: 1 }]} onPress={() => goTo('PostJobScreen')}>
          <MaterialCommunityIcons name="briefcase-plus" size={16} color="#fff" />
          <Text style={S.primaryBtnText}>Post New Job</Text>
        </TouchableOpacity>
        <TouchableOpacity style={S.iconBtnSq} onPress={() => goTo('EditJob')}>
          <MaterialCommunityIcons name="pencil" size={18} color="#3B82F6" />
        </TouchableOpacity>
      </View>

      {/* Jobs summary */}
      <View style={[S.kpiRow, { marginBottom: 12 }]}>
        {[
          { label: 'Total Jobs',  value: stats.totalJobs,  color: '#3B82F6', icon: 'briefcase' },
          { label: 'Active',      value: stats.activeJobs, color: '#10B981', icon: 'briefcase-check' },
          { label: 'Filled',      value: stats.hired,      color: '#059669', icon: 'check-circle' },
        ].map(k => <React.Fragment key={`jkpi-${k.label}`}>{renderStatCard(k.label, k.value, k.icon, k.color)}</React.Fragment>)}
      </View>

      {jobs.length === 0 ? (
        <View style={{ alignItems: 'center', paddingVertical: 48 }}>
          <MaterialCommunityIcons name="briefcase-outline" size={54} color="#CBD5E1" />
          <Text style={{ color: '#94A3B8', marginTop: 12, fontSize: 15 }}>No jobs found</Text>
          <TouchableOpacity style={[S.primaryBtn, { marginTop: 16, paddingHorizontal: 24 }]} onPress={() => goTo('PostJobScreen')}>
            <Text style={S.primaryBtnText}>Post First Job</Text>
          </TouchableOpacity>
        </View>
      ) : jobs.map((job, i) => {
        const jobApps = candidates.filter(c => c.job_id === job.id || c.job_title === job.title).length;
        const isActive = job.status === 'active' || job.is_active;
        return (
          <TouchableOpacity key={job.id || i} style={S.jobCard} onPress={() => setJobDetailModal(job)}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
              <View style={[S.jobIcon, { backgroundColor: isActive ? '#E0F2FE' : '#F1F5F9' }]}>
                <MaterialCommunityIcons name="briefcase-outline" size={20} color={isActive ? '#3B82F6' : '#94A3B8'} />
              </View>
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={S.jobTitle}>{job.title || '—'}</Text>
                <Text style={S.jobDept}>{job.department || job.location || '—'}</Text>
                <View style={{ flexDirection: 'row', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
                  <Pill label={isActive ? 'Active' : 'Closed'} color={isActive ? '#10B981' : '#94A3B8'} />
                  {job.job_type && <Pill label={job.job_type} color="#0EA5E9" />}
                  {job.salary_range && <Pill label={job.salary_range} color="#059669" />}
                </View>
              </View>
              <View style={{ alignItems: 'center' }}>
                <Text style={{ fontSize: 20, fontWeight: '900', color: '#3B82F6' }}>{jobApps}</Text>
                <Text style={{ fontSize: 10, color: '#94A3B8' }}>Applicants</Text>
              </View>
            </View>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 12, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#F1F5F9' }}>
              <TouchableOpacity style={[S.jobAction, { backgroundColor: '#E0F2FE' }]} onPress={() => goTo('ApplicantsPoolScreen', { jobId: job.id })}>
                <Text style={[S.jobActionText, { color: '#3B82F6' }]}>View Applicants</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[S.jobAction, { backgroundColor: '#F3E8FF' }]} onPress={() => goTo('EditJob', { jobId: job.id })}>
                <Text style={[S.jobActionText, { color: '#7C3AED' }]}>Edit</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[S.jobAction, { backgroundColor: '#DCFCE7' }]} onPress={() => goTo('InterviewManagementScreen', { jobId: job.id })}>
                <Text style={[S.jobActionText, { color: '#059669' }]}>Interviews</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  // ── Tab: Analytics ─────────────────────────────────────────────────────────
  const renderAnalytics = () => {
    const convRates = STAGES.slice(1).map((s, i) => {
      const prev = stats[STAGES[i].statKey] || 1;
      const curr = stats[s.statKey] || 0;
      return { label: `${STAGES[i].label} → ${s.label}`, rate: pct(curr, prev), color: s.color };
    });

    return (
      <View>
        {/* Key metrics */}
        <View style={S.card}>
          <Text style={S.cardTitle}>Key Metrics</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
            {[
              { label: 'Hire Rate',           value: `${hireRate}%`,   color: '#059669' },
              { label: 'Offer Acceptance',     value: `${pct(stats.accepted, stats.offered)}%`, color: '#7C3AED' },
              { label: 'Screen → Shortlist',   value: `${pct(stats.shortlisted, stats.screened)}%`, color: '#8B5CF6' },
              { label: 'Total Pipeline',       value: stats.totalApplicants, color: '#3B82F6' },
              { label: 'Actively Hiring',      value: stats.activeJobs,    color: '#10B981' },
              { label: 'Rejected',             value: stats.rejected,       color: '#EF4444' },
            ].map(m => (
              <View key={`kpi-${m.label}`} style={[S.metricBox, { borderTopColor: m.color }]}>
                <Text style={[S.metricValue, { color: m.color }]}>{m.value}</Text>
                <Text style={S.metricLabel}>{m.label}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Full funnel */}
        <View style={S.card}>
          <Text style={S.cardTitle}>Full Hiring Funnel</Text>
          <View style={{ marginTop: 12 }}>
            {STAGES.map(stage => (
              <View key={`afunnel-${stage.id}`} style={{ marginBottom: 10 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontSize: 13, color: '#374151', fontWeight: '600' }}>{stage.label}</Text>
                  <Text style={{ fontSize: 13, fontWeight: '700', color: stage.color }}>
                    {stats[stage.statKey] || 0} ({pct(stats[stage.statKey] || 0, stats.totalApplicants)}%)
                  </Text>
                </View>
                <View style={{ height: 10, backgroundColor: '#F1F5F9', borderRadius: 5, overflow: 'hidden' }}>
                  <Animated.View style={{
                    width: stats.totalApplicants > 0
                      ? `${pct(stats[stage.statKey] || 0, stats.totalApplicants)}%`
                      : '0%',
                    height: '100%', backgroundColor: stage.color, borderRadius: 5,
                  }} />
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Stage conversion rates */}
        <View style={S.card}>
          <Text style={S.cardTitle}>Stage Conversion Rates</Text>
          <View style={{ marginTop: 12 }}>
            {convRates.map((r, i) => (
              <View key={`conv-${i}`} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                <Text style={{ flex: 1, fontSize: 12, color: '#64748B' }} numberOfLines={1}>{r.label}</Text>
                <View style={{ width: 90, height: 7, backgroundColor: '#F1F5F9', borderRadius: 4, overflow: 'hidden', marginHorizontal: 8 }}>
                  <View style={{ width: `${r.rate}%`, height: '100%', backgroundColor: r.color, borderRadius: 4 }} />
                </View>
                <Text style={{ fontSize: 12, fontWeight: '700', color: r.color, width: 36, textAlign: 'right' }}>{r.rate}%</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Department breakdown */}
        {departments.length > 0 && (
          <View style={S.card}>
            <Text style={S.cardTitle}>By Department</Text>
            <View style={{ marginTop: 12 }}>
              {departments.map(dept => {
                const cnt = candidates.filter(c => c.department === dept).length;
                return (
                  <View key={`dept-${dept}`} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                    <Text style={{ flex: 1, fontSize: 13, color: '#374151' }}>{dept}</Text>
                    <View style={{ width: 80, height: 6, backgroundColor: '#F1F5F9', borderRadius: 3, overflow: 'hidden', marginRight: 8 }}>
                      <View style={{ width: `${pct(cnt, candidates.length)}%`, height: '100%', backgroundColor: '#3B82F6', borderRadius: 3 }} />
                    </View>
                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#3B82F6', width: 30, textAlign: 'right' }}>{cnt}</Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* Quick export */}
        <TouchableOpacity style={[S.primaryBtn, { marginVertical: 8, marginHorizontal: 0 }]} onPress={exportReport}>
          <MaterialCommunityIcons name="share-variant" size={16} color="#fff" />
          <Text style={S.primaryBtnText}>Export Recruitment Report</Text>
        </TouchableOpacity>

        {/* Navigate to full analytics */}
        <TouchableOpacity style={[S.card, { flexDirection: 'row', alignItems: 'center', gap: 12 }]}
          onPress={() => goTo('CompanyAnalytics')}>
          <MaterialCommunityIcons name="chart-box" size={28} color="#7C3AED" />
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 15, fontWeight: '700', color: '#0F172A' }}>Advanced Analytics Dashboard</Text>
            <Text style={{ fontSize: 12, color: '#64748B' }}>Detailed reports, trends, forecasting</Text>
          </View>
          <Ionicons name="arrow-forward" size={18} color="#94A3B8" />
        </TouchableOpacity>
      </View>
    );
  };

  // ── Tab: Team ──────────────────────────────────────────────────────────────
  const renderTeam = () => (
    <View>
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
        <TouchableOpacity style={[S.primaryBtn, { flex: 1 }]} onPress={() => goTo('TeamManagementScreen')}>
          <MaterialCommunityIcons name="account-group" size={16} color="#fff" />
          <Text style={S.primaryBtnText}>Manage Team</Text>
        </TouchableOpacity>
        <TouchableOpacity style={S.iconBtnSq} onPress={() => goTo('BusinessUserProfileScreen')}>
          <MaterialCommunityIcons name="account-plus" size={18} color="#3B82F6" />
        </TouchableOpacity>
      </View>

      <View style={[S.kpiRow, { marginBottom: 12 }]}>
        {renderStatCard('Team Size', teamMembers.length, 'account-group', '#3B82F6')}
        {renderStatCard('Online', teamMembers.filter(m => m.status === 'online' || m.is_online).length, 'radio-button-on', '#10B981')}
        {renderStatCard('Active Recs', teamMembers.reduce((s, m) => s + safeNum(m.activeRecruitments), 0), 'briefcase', '#F59E0B')}
      </View>

      {teamMembers.length === 0 ? (
        <View style={{ alignItems: 'center', paddingVertical: 48 }}>
          <MaterialCommunityIcons name="account-group-outline" size={54} color="#CBD5E1" />
          <Text style={{ color: '#94A3B8', marginTop: 12 }}>No team members yet</Text>
        </View>
      ) : teamMembers.map((m, i) => (
        <View key={m.id ? `tm-${m.id}` : `tm-${i}`} style={S.teamCard}>
          <View style={{ position: 'relative' }}>
            <Avatar name={m.name || '?'} size={48} color={avatarColor(m.name || '')} />
            <View style={[S.onlineDot, { backgroundColor: m.status === 'online' || m.is_online ? '#10B981' : '#94A3B8' }]} />
          </View>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={S.teamName}>{m.name || '—'}</Text>
            <Text style={S.teamRole}>{m.role || '—'} · {m.department || '—'}</Text>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 4 }}>
              <Text style={{ fontSize: 11, color: '#3B82F6', fontWeight: '600' }}>
                {safeNum(m.activeRecruitments)} Active
              </Text>
              {m.email && <Text style={{ fontSize: 11, color: '#64748B' }}>{m.email}</Text>}
            </View>
          </View>
          <TouchableOpacity style={[S.smallBtn, { backgroundColor: '#3B82F6' }]}
            onPress={() => goTo('BusinessVideoConferenceScreen', { participantName: m.name })}>
            <Ionicons name="videocam" size={13} color="#fff" />
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );

  // ── Candidate Detail Modal ─────────────────────────────────────────────────
  const renderCandidateModal = () => {
    const c = candidateModal;
    if (!c) return null;
    const stage    = STAGES.find(s => s.id === (c.stage || c.status)) || STAGES[1];
    const stageIdx = STAGES.findIndex(s => s.id === stage.id);
    const nextStage = STAGES[stageIdx + 1];
    const aiScore  = safeNum(c.ai_score || c.score);

    return (
      <Modal visible={!!candidateModal} animationType="slide" presentationStyle="pageSheet">
        <View style={S.modalWrap}>
          <View style={S.modalHdr}>
            <TouchableOpacity onPress={() => setCandidateModal(null)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
            <Text style={S.modalHdrTitle} numberOfLines={1}>{c.candidate_name || 'Candidate'}</Text>
            <TouchableOpacity onPress={() => { setCandidateModal(null); setNotesModal(c.id); setNoteText(c.notes || ''); }}>
              <Ionicons name="document-text-outline" size={22} color="#3B82F6" />
            </TouchableOpacity>
          </View>

          <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 100 }}>
            {/* Hero */}
            <View style={{ alignItems: 'center', paddingVertical: 20, backgroundColor: stage.color + '10', borderRadius: 16, marginBottom: 16 }}>
              <Avatar name={c.candidate_name || '?'} size={72} color={stage.color} />
              <Text style={{ fontSize: 20, fontWeight: '900', color: '#0F172A', marginTop: 10 }}>{c.candidate_name || '—'}</Text>
              <Text style={{ fontSize: 14, color: '#64748B', marginTop: 2 }}>{c.job_title || '—'}</Text>
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 10 }}>
                <Pill label={stage.label} color={stage.color} />
                {c.department && <Pill label={c.department} color="#64748B" />}
              </View>
              {aiScore > 0 && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 12 }}>
                  <ScoreRing score={aiScore} size={56} />
                  <View>
                    <Text style={{ fontSize: 12, color: '#64748B' }}>AI Match Score</Text>
                    <Text style={{ fontSize: 11, color: '#94A3B8' }}>
                      <Text>{aiScore >= 80 ? 'Highly Recommended' : aiScore >= 60 ? 'Good Fit' : aiScore >= 40 ? 'Possible Fit' : 'Low Match'}</Text>
                    </Text>
                  </View>
                </View>
              )}
            </View>

            {/* Details */}
            <View style={S.detailCard}>
              <Text style={S.detailSectionTitle}>Contact & Details</Text>
              {[
                { icon: 'mail-outline',     label: 'Email',      value: c.email      },
                { icon: 'call-outline',     label: 'Phone',      value: c.phone      },
                { icon: 'location-outline', label: 'Location',   value: c.location   },
                { icon: 'school-outline',   label: 'Education',  value: c.education  },
                { icon: 'briefcase-outline',label: 'Experience', value: c.experience_years ? `${c.experience_years} years` : c.experience },
                { icon: 'calendar-outline', label: 'Applied',    value: fmtDate(c.created_at) },
                { icon: 'currency-usd',     label: 'Expected',   value: c.expected_salary || c.salary_expectation },
              ].filter(r => r.value).map(r => (
                <View key={`cdet-${r.label}`} style={S.detailRow}>
                  <Ionicons name={r.icon} size={15} color="#3B82F6" style={{ width: 22 }} />
                  <Text style={S.detailLabel}>{r.label}</Text>
                  <Text style={S.detailValue} numberOfLines={2}>{r.value}</Text>
                </View>
              ))}
            </View>

            {/* Skills */}
            {(c.skills || c.skill_tags) && (
              <View style={[S.detailCard, { marginTop: 8 }]}>
                <Text style={S.detailSectionTitle}>Skills</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {(Array.isArray(c.skills) ? c.skills : (c.skills || c.skill_tags || '').split(',')).map((s, i) => (
                    <View key={`skill-${i}`} style={{ backgroundColor: '#E0F2FE', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 4 }}>
                      <Text style={{ fontSize: 12, color: '#0EA5E9', fontWeight: '600' }}>{String(s).trim()}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Notes */}
            {c.notes && (
              <View style={[S.detailCard, { marginTop: 8 }]}>
                <Text style={S.detailSectionTitle}>Notes</Text>
                <Text style={{ fontSize: 13, color: '#374151', marginTop: 6, lineHeight: 19 }}>{c.notes}</Text>
              </View>
            )}

            {/* Actions */}
            <View style={{ marginTop: 16 }}>
              <Text style={[S.detailSectionTitle, { marginBottom: 10 }]}>Actions</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {nextStage && (
                  <TouchableOpacity style={[S.actionChip, { backgroundColor: nextStage.color + '15', borderColor: nextStage.color }]}
                    onPress={() => { setCandidateModal(null); advanceCandidate(c.id, nextStage.id); }}>
                    <MaterialCommunityIcons name="arrow-right-circle" size={15} color={nextStage.color} />
                    <Text style={[S.actionChipText, { color: nextStage.color }]}>Advance to {nextStage.label}</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity style={[S.actionChip, { backgroundColor: '#E0F2FE', borderColor: '#0EA5E9' }]}
                  onPress={() => { setCandidateModal(null); scheduleInterview(c.id, 'video'); }}>
                  <Ionicons name="videocam-outline" size={15} color="#0EA5E9" />
                  <Text style={[S.actionChipText, { color: '#0EA5E9' }]}>Video Interview</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[S.actionChip, { backgroundColor: '#F3E8FF', borderColor: '#7C3AED' }]}
                  onPress={() => { setCandidateModal(null); scheduleInterview(c.id, 'in-person'); }}>
                  <MaterialCommunityIcons name="account-tie-outline" size={15} color="#7C3AED" />
                  <Text style={[S.actionChipText, { color: '#7C3AED' }]}>Final Interview</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[S.actionChip, { backgroundColor: '#DCFCE7', borderColor: '#059669' }]}
                  onPress={() => { setCandidateModal(null); goTo('JobOfferManagementScreen', { candidateId: c.id }); }}>
                  <MaterialCommunityIcons name="file-document-outline" size={15} color="#059669" />
                  <Text style={[S.actionChipText, { color: '#059669' }]}>Make Offer</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[S.actionChip, { backgroundColor: '#FEF9C3', borderColor: '#F59E0B' }]}
                  onPress={() => { setCandidateModal(null); setNotesModal(c.id); setNoteText(c.notes || ''); }}>
                  <MaterialCommunityIcons name="note-plus-outline" size={15} color="#F59E0B" />
                  <Text style={[S.actionChipText, { color: '#F59E0B' }]}>Add Note</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[S.actionChip, { backgroundColor: '#FEE2E2', borderColor: '#EF4444' }]}
                  onPress={() => { setCandidateModal(null); rejectCandidate(c.id, c.candidate_name || 'this candidate'); }}>
                  <MaterialCommunityIcons name="close-circle-outline" size={15} color="#EF4444" />
                  <Text style={[S.actionChipText, { color: '#EF4444' }]}>Reject</Text>
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>
        </View>
      </Modal>
    );
  };

  // ── Stage Detail Modal ─────────────────────────────────────────────────────
  const renderStageModal = () => {
    const stage    = STAGES.find(s => s.id === stageModal);
    if (!stage) return null;
    const stageCandidates = candidates.filter(c => (c.stage || c.status) === stageModal);

    return (
      <Modal visible={!!stageModal} animationType="slide" presentationStyle="pageSheet">
        <View style={S.modalWrap}>
          <View style={[S.modalHdr, { borderBottomColor: stage.color + '30' }]}>
            <TouchableOpacity onPress={() => setStageModal(null)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
            <View>
              <Text style={S.modalHdrTitle}>{stage.label}</Text>
              <Text style={{ fontSize: 12, color: '#94A3B8', textAlign: 'center' }}>{stageCandidates.length} candidates</Text>
            </View>
            <TouchableOpacity style={[S.smallBtn, { backgroundColor: stage.color }]}
              onPress={() => { setStageModal(null); goTo(STAGE_SCREENS[stage.id]); }}>
              <Text style={S.smallBtnText}>Open</Text>
            </TouchableOpacity>
          </View>

          <ScrollView contentContainerStyle={{ padding: 14 }}>
            {stageCandidates.length === 0 ? (
              <View style={{ alignItems: 'center', paddingVertical: 48 }}>
                <MaterialCommunityIcons name={stage.icon} size={54} color={stage.color + '50'} />
                <Text style={{ color: '#94A3B8', marginTop: 12 }}>No candidates at this stage</Text>
              </View>
            ) : stageCandidates.map(c => (
              <TouchableOpacity key={c.id} style={S.candidateCard}
                onPress={() => { setStageModal(null); setCandidateModal(c); }}>
                <Avatar name={c.candidate_name || '?'} size={40} />
                <View style={{ flex: 1, marginLeft: 10 }}>
                  <Text style={S.candidateName}>{c.candidate_name || '—'}</Text>
                  <Text style={S.candidateRole}>{c.job_title || '—'}</Text>
                </View>
                {safeNum(c.ai_score) > 0 && <ScoreRing score={safeNum(c.ai_score)} size={34} />}
                <Ionicons name="chevron-forward" size={16} color="#CBD5E1" style={{ marginLeft: 6 }} />
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </Modal>
    );
  };

  // ── Notes Modal ────────────────────────────────────────────────────────────
  const renderNotesModal = () => (
    <Modal visible={!!notesModal} animationType="slide" transparent>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={{ backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24 }}>
            <Text style={{ fontSize: 16, fontWeight: '800', color: '#0F172A', marginBottom: 14 }}>Add Note</Text>
            <TextInput style={[S.noteInput]} value={noteText} onChangeText={setNoteText}
              placeholder="Enter notes about this candidate…" placeholderTextColor="#CBD5E1"
              multiline numberOfLines={4} autoFocus />
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 14 }}>
              <TouchableOpacity style={[S.modalBtn, { backgroundColor: '#F1F5F9', flex: 1 }]}
                onPress={() => { setNotesModal(null); setNoteText(''); }}>
                <Text style={{ color: '#64748B', fontWeight: '700' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[S.modalBtn, { backgroundColor: '#3B82F6', flex: 1 }]}
                onPress={() => saveNote(notesModal)} disabled={savingNote}>
                {savingNote ? <ActivityIndicator color="#fff" size={16} />
                  : <Text style={{ color: '#fff', fontWeight: '700' }}>Save Note</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  // ── Search Modal ───────────────────────────────────────────────────────────
  const renderSearchModal = () => (
    <Modal visible={showSearch} animationType="fade" transparent>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.3)', paddingTop: 60 }}>
        <View style={{ backgroundColor: '#fff', margin: 16, borderRadius: 16, padding: 16 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <Ionicons name="search" size={18} color="#3B82F6" />
            <TextInput style={{ flex: 1, fontSize: 15, color: '#0F172A' }} value={searchQ}
              onChangeText={setSearchQ} placeholder="Search by name, role, department…"
              autoFocus placeholderTextColor="#CBD5E1" />
            <TouchableOpacity onPress={() => { setSearchQ(''); setShowSearch(false); }}>
              <Ionicons name="close-circle" size={20} color="#94A3B8" />
            </TouchableOpacity>
          </View>
          {/* Dept filter */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
            {['all', ...departments].map(d => (
              <TouchableOpacity key={`dfilt-${d}`} style={[S.stageChip, filterDept === d && S.stageChipActive]}
                onPress={() => setFilterDept(d)}>
                <Text style={[S.stageChipText, filterDept === d && { color: '#fff' }]}>
                  {d === 'all' ? 'All Depts' : d}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <TouchableOpacity style={[S.primaryBtn, { marginTop: 12 }]}
            onPress={() => setShowSearch(false)}>
            <Text style={S.primaryBtnText}>Apply Filters</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ── Stats Modal ────────────────────────────────────────────────────────────
  const renderStatsModal = () => (
    <Modal visible={statsModal} animationType="slide" presentationStyle="pageSheet">
      <View style={S.modalWrap}>
        <View style={S.modalHdr}>
          <TouchableOpacity onPress={() => setStatsModal(false)}>
            <Ionicons name="close" size={24} color="#64748B" />
          </TouchableOpacity>
          <Text style={S.modalHdrTitle}>Recruitment Statistics</Text>
          <View style={{ width: 24 }} />
        </View>
        <ScrollView contentContainerStyle={{ padding: 16 }}>
          <View style={[S.kpiRow, { marginBottom: 8 }]}>
            {[
              { l: 'Total Jobs',    v: stats.totalJobs,       c: '#3B82F6', i: 'briefcase' },
              { l: 'Active Jobs',   v: stats.activeJobs,      c: '#10B981', i: 'briefcase-check' },
              { l: 'Applicants',    v: stats.totalApplicants, c: '#8B5CF6', i: 'account-group' },
              { l: 'New Today',     v: stats.newToday,        c: '#F59E0B', i: 'account-plus' },
              { l: 'Shortlisted',   v: stats.shortlisted,     c: '#0EA5E9', i: 'account-star' },
              { l: 'Interviewed',   v: stats.videoInt + stats.finalInt, c: '#DC2626', i: 'video' },
              { l: 'Offered',       v: stats.offered,         c: '#7C3AED', i: 'file-document' },
              { l: 'Hired',         v: stats.hired,           c: '#059669', i: 'check-circle' },
              { l: 'Rejected',      v: stats.rejected,        c: '#EF4444', i: 'close-circle' },
            ].map((k) => <React.Fragment key={k.l}>{renderStatCard(k.l, k.v, k.i, k.c, undefined)}</React.Fragment>)}
          </View>
        </ScrollView>
      </View>
    </Modal>
  );

  // ── Activities Modal ───────────────────────────────────────────────────────
  const renderActivitiesModal = () => (
    <Modal visible={activitiesModal} animationType="slide" presentationStyle="pageSheet">
      <View style={S.modalWrap}>
        <View style={S.modalHdr}>
          <TouchableOpacity onPress={() => setActivitiesModal(false)}>
            <Ionicons name="close" size={24} color="#64748B" />
          </TouchableOpacity>
          <Text style={S.modalHdrTitle}>Recent Activity</Text>
          <View style={{ width: 24 }} />
        </View>
        <FlatList
          data={activities.length > 0 ? activities : getDefaultActivities()}
          keyExtractor={(item, i) => String(item.id || i)}
          contentContainerStyle={{ padding: 14 }}
          renderItem={({ item: a }) => (
            <View style={S.activityCardFull}>
              <View style={[S.activityDot, { backgroundColor: a.color || '#3B82F6', width: 36, height: 36, borderRadius: 18 }]}>
                <MaterialCommunityIcons name={a.icon || 'information'} size={16} color="#fff" />
              </View>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={S.activityTitle}>{a.title}</Text>
                <Text style={S.activityDesc}>{a.description}</Text>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                  <Text style={{ fontSize: 11, color: '#3B82F6', fontWeight: '500' }}>{a.user || 'System'}</Text>
                  <Text style={S.activityTime}>{timeAgo(a.timestamp || a.created_at || new Date())}</Text>
                </View>
              </View>
            </View>
          )}
        />
      </View>
    </Modal>
  );

  // ── Job Detail Modal ───────────────────────────────────────────────────────
  const renderJobDetailModal = () => {
    const job = jobDetailModal;
    if (!job) return null;
    const jobApps = candidates.filter(c => c.job_id === job.id || c.job_title === job.title);

    return (
      <Modal visible={!!jobDetailModal} animationType="slide" presentationStyle="pageSheet">
        <View style={S.modalWrap}>
          <View style={S.modalHdr}>
            <TouchableOpacity onPress={() => setJobDetailModal(null)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
            <Text style={S.modalHdrTitle} numberOfLines={1}>{job.title || 'Job'}</Text>
            <TouchableOpacity onPress={() => { setJobDetailModal(null); goTo('EditJob', { jobId: job.id }); }}>
              <Text style={{ fontSize: 14, color: '#3B82F6', fontWeight: '700' }}>Edit</Text>
            </TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={{ padding: 16 }}>
            <View style={S.detailCard}>
              {[
                { label: 'Department', value: job.department },
                { label: 'Location',   value: job.location },
                { label: 'Type',       value: job.job_type },
                { label: 'Salary',     value: job.salary_range },
                { label: 'Posted',     value: fmtDate(job.created_at) },
                { label: 'Deadline',   value: fmtDate(job.application_deadline) },
              ].filter(r => r.value).map(r => (
                <View key={`jdet-${r.label}`} style={S.detailRow}>
                  <Text style={S.detailLabel}>{r.label}</Text>
                  <Text style={S.detailValue}>{r.value}</Text>
                </View>
              ))}
            </View>
            {job.description && (
              <View style={[S.detailCard, { marginTop: 8 }]}>
                <Text style={S.detailSectionTitle}>Description</Text>
                <Text style={{ fontSize: 13, color: '#374151', lineHeight: 20, marginTop: 8 }}>{job.description}</Text>
              </View>
            )}
            <Text style={[S.detailSectionTitle, { marginTop: 16, marginBottom: 8 }]}>
              Applicants ({jobApps.length})
            </Text>
            {jobApps.slice(0, 5).map(c => (
              <TouchableOpacity key={c.id ? `japp-${c.id}` : `japp-${Math.random()}`} style={S.candidateCard}
                onPress={() => { setJobDetailModal(null); setCandidateModal(c); }}>
                <Avatar name={c.candidate_name || '?'} size={36} />
                <View style={{ flex: 1, marginLeft: 8 }}>
                  <Text style={S.candidateName}>{c.candidate_name || '—'}</Text>
                  <Pill label={(STAGES.find(s => s.id === c.stage) || STAGES[1]).label}
                    color={(STAGES.find(s => s.id === c.stage) || STAGES[1]).color} />
                </View>
              </TouchableOpacity>
            ))}
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
              <TouchableOpacity style={[S.primaryBtn, { flex: 1 }]}
                onPress={() => { setJobDetailModal(null); goTo('ApplicantsPoolScreen', { jobId: job.id }); }}>
                <Text style={S.primaryBtnText}>View All Applicants</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </Modal>
    );
  };

  // ── Default activities fallback ────────────────────────────────────────────
  const getDefaultActivities = () => [
    { id: '1', title: 'AI Pipeline Processed', description: '15 candidates automatically scored', icon: 'robot', color: '#FF6B35', timestamp: new Date(Date.now() - 300000).toISOString(), user: 'AI System' },
    { id: '2', title: 'New Application', description: 'Sarah Johnson applied for Senior Developer', icon: 'account-plus', color: '#10B981', timestamp: new Date(Date.now() - 900000).toISOString(), user: 'System' },
    { id: '3', title: 'Interview Scheduled', description: 'Video interview with James Lee at 2pm', icon: 'calendar-clock', color: '#3B82F6', timestamp: new Date(Date.now() - 3600000).toISOString(), user: 'HR Team' },
    { id: '4', title: 'Offer Sent', description: 'Job offer sent to Emma Davis', icon: 'file-document', color: '#7C3AED', timestamp: new Date(Date.now() - 7200000).toISOString(), user: 'John Smith' },
    { id: '5', title: 'Candidate Shortlisted', description: 'Mark Wilson added to shortlist', icon: 'account-star', color: '#F59E0B', timestamp: new Date(Date.now() - 10800000).toISOString(), user: 'HR Team' },
  ];

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <View style={S.loadingWrap}>
        <ActivityIndicator size={40} color="#3B82F6" />
        <Text style={S.loadingTitle}>Loading Recruitment Flow</Text>
        <Text style={S.loadingSubtitle}>Syncing with live data…</Text>
      </View>
    );
  }

  const TABS = [
    { id: 'overview',  label: 'Overview',  icon: 'view-dashboard' },
    { id: 'pipeline',  label: 'Pipeline',  icon: 'account-convert' },
    { id: 'jobs',      label: 'Jobs',      icon: 'briefcase' },
    { id: 'analytics', label: 'Analytics', icon: 'chart-line' },
    { id: 'team',      label: 'Team',      icon: 'account-group' },
  ];

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <View style={S.container}>
      <StatusBar barStyle="light-content" backgroundColor="#1D4ED8" />

      {/* Header */}
      <LinearGradient colors={['#1D4ED8','#3B82F6']} style={S.header}>
        <View style={S.headerRow}>
          <TouchableOpacity style={S.headerBtn} onPress={() => navigation?.goBack()}>
            <Ionicons name="arrow-back" size={22} color="#fff" />
          </TouchableOpacity>
          <View style={{ flex: 1, alignItems: 'center' }}>
            {companyLogo ? (
                <TouchableOpacity onPress={() => navigation?.navigate('CompanyProfileScreen')}>
                  <Image source={{ uri: companyLogo || undefined }} style={{ width: 34, height: 34, borderRadius: 8 }} resizeMode="cover" />
                </TouchableOpacity>
              ) : null}
              <Text style={S.headerTitle}>Recruitment Flow</Text>
            <Text style={S.headerSub}>
              {stats.activeJobs} active jobs · {stats.totalApplicants} applicants
            </Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <TouchableOpacity style={S.headerBtn} onPress={() => setShowSearch(true)}>
              <Ionicons name="search" size={20} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={S.headerBtn} onPress={() => setStatsModal(true)}>
              <MaterialCommunityIcons name="chart-line" size={20} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={S.headerBtn} onPress={exportReport}>
              <Ionicons name="share-outline" size={20} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>
      </LinearGradient>

      {/* Tab bar */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        style={S.tabBar} contentContainerStyle={{ paddingHorizontal: 12 }}>
        {TABS.map(t => (
          <TouchableOpacity key={`tab-${t.id}`} style={[S.tab, activeTab === t.id && S.tabActive]}
            onPress={() => setActiveTab(t.id)}>
            <MaterialCommunityIcons name={t.icon} size={16} color={activeTab === t.id ? '#3B82F6' : '#94A3B8'} />
            <Text style={[S.tabText, activeTab === t.id && S.tabTextActive]}>{t.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Content */}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={S.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#3B82F6']} />}
        showsVerticalScrollIndicator={false}>
        {activeTab === 'overview'  && renderOverview()}
        {activeTab === 'pipeline'  && renderPipeline()}
        {activeTab === 'jobs'      && renderJobs()}
        {activeTab === 'analytics' && renderAnalytics()}
        {activeTab === 'team'      && renderTeam()}
      </ScrollView>

      {/* FAB */}
      <TouchableOpacity style={S.fab} onPress={() => goTo('PostJobScreen')}>
        <MaterialCommunityIcons name="briefcase-plus" size={24} color="#fff" />
      </TouchableOpacity>

      {/* All modals */}
      {renderCandidateModal()}
      {renderStageModal()}
      {renderNotesModal()}
      {renderSearchModal()}
      {renderStatsModal()}
      {renderActivitiesModal()}
      {renderJobDetailModal()}
    </View>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  container:     { flex: 1, backgroundColor: '#F8FAFC' },
  loadingWrap:   { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC' },
  loadingTitle:  { fontSize: 18, fontWeight: '700', color: '#0F172A', marginTop: 16 },
  loadingSubtitle:{ fontSize: 13, color: '#64748B', marginTop: 4 },

  // Header
  header:      { paddingTop: Platform.OS === 'ios' ? 50 : 36, paddingBottom: 14, paddingHorizontal: 16 },
  headerRow:   { flexDirection: 'row', alignItems: 'center', gap: 8 },
  headerBtn:   { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.15)', alignItems: 'center', justifyContent: 'center' },
  headerTitle: { fontSize: 18, fontWeight: '900', color: '#fff' },
  headerSub:   { fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 1 },

  // Tab bar
  tabBar:      { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E2E8F0', maxHeight: 48 },
  tab:         { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 14, paddingVertical: 12, marginRight: 2 },
  tabActive:   { borderBottomWidth: 2, borderBottomColor: '#3B82F6' },
  tabText:     { fontSize: 13, color: '#94A3B8', fontWeight: '600' },
  tabTextActive:{ color: '#3B82F6', fontWeight: '800' },

  scrollContent: { padding: 16, paddingBottom: 90 },

  // Cards
  card:       { backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 14,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 8, elevation: 3 },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  cardTitle:  { fontSize: 16, fontWeight: '800', color: '#0F172A' },
  seeAll:     { fontSize: 13, color: '#3B82F6', fontWeight: '600' },

  // KPI grid
  kpiRow:     { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 14 },
  statCard:   { width: (width - 52) / 2, backgroundColor: '#fff', borderRadius: 14, padding: 14, borderLeftWidth: 4,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 5, elevation: 2 },
  statIcon:   { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  statValue:  { fontSize: 26, fontWeight: '900', color: '#0F172A' },
  statLabel:  { fontSize: 11, color: '#64748B', marginTop: 2 },

  // AI card
  aiCard:     { borderRadius: 16, overflow: 'hidden', marginBottom: 14,
    shadowColor: '#FF6B35', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.2, shadowRadius: 10, elevation: 6 },
  aiGradient: { padding: 18 },
  aiLaunchBtn:{ backgroundColor: '#fff', borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8,
    flexDirection: 'row', alignItems: 'center', gap: 4 },

  // Filter bar
  filterBar:   { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  searchTrigger: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#F1F5F9',
    borderRadius: 12, paddingHorizontal: 12, paddingVertical: 10, gap: 8 },
  filterBtn:   { width: 38, height: 38, borderRadius: 10, backgroundColor: '#F1F5F9', alignItems: 'center', justifyContent: 'center' },

  // Stage chips
  stageChip:     { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, backgroundColor: '#F1F5F9',
    borderWidth: 1, borderColor: '#E2E8F0' },
  stageChipActive: { backgroundColor: '#3B82F6', borderColor: '#3B82F6' },
  stageChipText: { fontSize: 12, color: '#64748B', fontWeight: '600' },

  // Sort
  sortBtn:     { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14, backgroundColor: '#F8FAFC', borderWidth: 1, borderColor: '#E2E8F0' },
  sortBtnActive: { backgroundColor: '#EFF6FF', borderColor: '#3B82F6' },
  sortBtnText: { fontSize: 11, color: '#64748B', fontWeight: '600' },

  // Bulk bar
  bulkBar:     { flexDirection: 'row', alignItems: 'center', backgroundColor: '#1D4ED8', borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 10, marginBottom: 10, gap: 8 },
  bulkAction:  { backgroundColor: '#059669', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 },
  bulkActionText: { color: '#fff', fontSize: 12, fontWeight: '700' },

  // Candidate card
  candidateCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 14,
    padding: 12, marginBottom: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  candidateCardSelected: { borderWidth: 2, borderColor: '#3B82F6', backgroundColor: '#EFF6FF' },
  candidateName: { fontSize: 14, fontWeight: '700', color: '#0F172A', flex: 1 },
  candidateRole: { fontSize: 12, color: '#64748B', marginTop: 2 },

  // Job card
  jobCard:    { backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 10,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 5, elevation: 2 },
  jobIcon:    { width: 44, height: 44, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  jobTitle:   { fontSize: 15, fontWeight: '700', color: '#0F172A' },
  jobDept:    { fontSize: 12, color: '#64748B', marginTop: 2 },
  jobAction:  { flex: 1, paddingVertical: 8, borderRadius: 8, alignItems: 'center' },
  jobActionText: { fontSize: 12, fontWeight: '700' },

  // Interview / offer rows
  interviewRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: '#F8FAFC' },
  interviewName: { fontSize: 14, fontWeight: '600', color: '#0F172A' },
  interviewMeta: { fontSize: 12, color: '#64748B', marginTop: 1 },
  offerRow:   { flexDirection: 'row', alignItems: 'center', paddingVertical: 10 },

  // Team card
  teamCard:   { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 14,
    padding: 14, marginBottom: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  teamName:   { fontSize: 14, fontWeight: '700', color: '#0F172A' },
  teamRole:   { fontSize: 12, color: '#64748B', marginTop: 1 },
  onlineDot:  { position: 'absolute', bottom: 1, right: 1, width: 12, height: 12, borderRadius: 6, borderWidth: 2, borderColor: '#fff' },

  // Activity
  activityRow:  { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: '#F8FAFC' },
  activityDot:  { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  activityTitle:{ fontSize: 13, fontWeight: '600', color: '#0F172A' },
  activityDesc: { fontSize: 12, color: '#64748B', marginTop: 1, lineHeight: 16 },
  activityTime: { fontSize: 11, color: '#94A3B8' },
  activityCardFull: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: '#F8FAFC' },

  // Pipeline rows
  pipelineRow:  { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingLeft: 8,
    borderLeftWidth: 3, marginBottom: 4 },
  pipelineIcon: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
  pipelineName: { fontSize: 14, fontWeight: '600', color: '#0F172A' },
  pipelineCount:{ width: 40, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  pipelineCountText: { fontSize: 14, fontWeight: '800' },

  // Analytics
  metricBox:  { width: (width - 52) / 2, backgroundColor: '#F8FAFC', borderRadius: 12, padding: 14,
    borderTopWidth: 3, borderColor: '#E2E8F0' },
  metricValue:{ fontSize: 22, fontWeight: '900' },
  metricLabel:{ fontSize: 11, color: '#64748B', marginTop: 3 },

  // Feature chips
  featureChip:{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8,
    borderRadius: 12, borderWidth: 1 },

  // Buttons
  primaryBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    backgroundColor: '#3B82F6', borderRadius: 12, paddingVertical: 12, paddingHorizontal: 16 },
  primaryBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  smallBtn:   { flexDirection: 'row', alignItems: 'center', gap: 3, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6 },
  smallBtnText:{ color: '#fff', fontSize: 12, fontWeight: '700' },
  iconBtnSq:  { width: 44, height: 44, borderRadius: 12, backgroundColor: '#EFF6FF', alignItems: 'center', justifyContent: 'center' },

  // Action chips
  actionChip:  { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 8,
    borderRadius: 16, borderWidth: 1.5, marginBottom: 6 },
  actionChipText: { fontSize: 12, fontWeight: '700' },

  // FAB
  fab:        { position: 'absolute', bottom: 24, right: 20, width: 56, height: 56, borderRadius: 28,
    backgroundColor: '#3B82F6', alignItems: 'center', justifyContent: 'center',
    shadowColor: '#3B82F6', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.4, shadowRadius: 10, elevation: 8 },

  // Modals
  modalWrap:   { flex: 1, backgroundColor: '#F8FAFC' },
  modalHdr:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14, backgroundColor: '#fff',
    borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  modalHdrTitle: { flex: 1, fontSize: 16, fontWeight: '800', color: '#0F172A', textAlign: 'center', marginHorizontal: 8 },
  modalBtn:    { flex: 1, paddingVertical: 13, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },

  // Detail modal
  detailCard:  { backgroundColor: '#fff', borderRadius: 14, padding: 14 },
  detailSectionTitle: { fontSize: 12, fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.6 },
  detailRow:   { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F8FAFC' },
  detailLabel: { fontSize: 12, color: '#94A3B8', fontWeight: '600', width: 90 },
  detailValue: { flex: 1, fontSize: 13, color: '#374151', fontWeight: '500' },

  // Note input
  noteInput:   { backgroundColor: '#F8FAFC', borderWidth: 1.5, borderColor: '#E2E8F0', borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 12, fontSize: 14, color: '#0F172A', minHeight: 100, textAlignVertical: 'top' },
});